/**
 * Лента визуалов — единый редактор порядка и состава фото карточки.
 *
 * Развитие `CardMediaEditor`: тот же набор адресов в порядке отправки, те же
 * загрузка и удаление, но с тем, чего не хватало в окнах сущностей:
 * происхождение и роль кадра, отметки расхождения с исходным набором
 * («новое», «перемещено», «заменено»), меню слота, клавиатура, замена файла
 * перетаскиванием, сравнение с витриной и жёлтая строка изменений.
 *
 * Лента не знает, куда сохраняется набор: сохраняет потребитель (футер окна,
 * кнопка менеджера), лента лишь отдаёт новый массив адресов через `onChange`.
 * Охват правки («эта карточка» / «товар в кабинете» / «вся тематика») тоже
 * задаёт потребитель: лента рисует переключатель и фразу «затронет …».
 *
 * Порядок значим: первым уходит главное фото карточки.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle,
  Columns2,
  Download,
  ImagePlus,
  Loader2,
  Maximize2,
  MoreVertical,
  Pencil,
  Replace,
  Sparkles,
  Star,
  Store,
  Trash2,
  Upload,
  Workflow,
} from 'lucide-react'
import { api } from '../../../../shared/api/client'
import { downloadAuthorizedMedia } from '../../../../shared/media/authorizedMedia'
import { Button } from '../../../../components/ui/Button'
import { SegmentedControl } from '../../../../components/ui/SegmentedControl'
import { cn } from '../../../../lib/cn'
import { AuthorizedLightbox } from '../AuthorizedLightbox'
import {
  ASPECT_CLASS,
  MARK_LABEL,
  ORIGIN_LABEL,
  affectedCardsText,
  compareRemote,
  deriveVisualMeta,
  diffRibbon,
  fileNameOf,
  type CompareState,
  type RibbonAspect,
  type RibbonOrigin,
  type SlotMark,
} from './model'

export type { RibbonAspect, RibbonOrigin } from './model'

export interface CardMediaUploadResult {
  url: string
  sha256?: string | null
  verified?: boolean
  file: File
}

export interface RibbonScopeOption {
  key: string
  label: string
  hint?: string
  disabled?: boolean
}

export interface MediaRibbonProps {
  /** Адреса в порядке отправки на площадку. */
  urls: string[]
  onChange: (urls: string[]) => void
  /** Артикул товара: под него кладётся загруженный файл. */
  article: string
  /** Код кабинета — визуалы у кабинетов свои. */
  cabinet?: string | null
  /** Чем рисовать: у черновика файлы за гейтвеем и требуют токен. */
  renderImage?: (src: string, alt: string, className: string) => React.ReactNode
  /** Как превратить адрес из набора в адрес для показа. */
  resolveSrc?: (url: string) => string
  /** Открыть кадр в полном размере. Без обработчика лента открывает свой просмотр. */
  onOpen?: (index: number) => void
  /** Предел площадки: у ВБ 30 фотографий на карточку. */
  maxCount?: number
  /** Пояснение над сеткой: у каждого случая своё. */
  hint?: React.ReactNode
  /** Загрузка товара идёт тем же загрузчиком за правом Product:UPDATE. */
  uploadEndpoint?: string
  /** Отдаёт менеджеру товара хеш и исходные байты для копии «в этой сессии». */
  onUploaded?: (result: CardMediaUploadResult) => void
  /** Дополнительная отметка плитки (происхождение/статус от потребителя). */
  renderMeta?: (url: string, index: number) => React.ReactNode
  /** У товара нельзя отвязать последнее оставшееся фото. */
  canRemove?: (url: string, index: number) => boolean
  removeDisabledReason?: string
  emptyText?: string
  disabled?: boolean

  /** Геометрия превью; не задаёт требования площадки к файлу. */
  aspect?: RibbonAspect
  /** Исходный набор — с ним сравниваются отметки и строка изменений. Нет — отметок нет. */
  baselineUrls?: string[]
  /** Происхождение кадра, если потребитель знает его точнее адреса. */
  originOf?: (url: string, index: number) => RibbonOrigin | null | undefined
  /** Роль кадра, если потребитель знает её (название слайда воронки). */
  roleOf?: (url: string, index: number) => string | null | undefined
  /** Что на площадке сейчас: включает кнопку «Сравнить с площадкой». */
  remoteUrls?: string[] | null

  /** Охват правки: варианты задаёт потребитель. */
  scopes?: RibbonScopeOption[]
  scope?: string
  onScopeChange?: (key: string) => void
  /** Заменяет сетку и панель инструментов (например, у охвата «вся тематика»). */
  scopeContent?: React.ReactNode
  /** Сколько карточек затронет сохранение. */
  affectedCards?: number | null
  /** Своя фраза вместо «N карточек» («только набор в ПИМ»). */
  affectedText?: string

  /** Кнопка сохранения в строке изменений; без обработчика её нет. */
  onSave?: () => void
  saveLabel?: string
  saving?: boolean
  saveDisabled?: boolean
}

/** Что принимает площадка. PNG и WEBP ВБ конвертирует сама, остальное отклоняет. */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp']
/** 10 МБ — предел ВБ на файл; больше площадка не примет, и грузить незачем. */
const MAX_BYTES = 10 * 1024 * 1024

const REGEN_FUNNEL_REASON =
  'Этот кадр собран воронкой — перегенерация всего набора: «Перегенерировать визуалы»'
const REGEN_SERIES_REASON =
  'Слайд серии контент-воронки: правка слайда идёт на странице запуска серии, отсюда запуск не определить'
const REGEN_MANUAL_REASON = 'Загруженный файл воронка не собирает: замените его через «Заменить…»'

const MARK_CLASS: Record<SlotMark, string> = {
  new: 'bg-green-600/90 text-white',
  moved: 'bg-amber-500/90 text-white',
  replaced: 'bg-sky-600/90 text-white',
}

const COMPARE_LABEL: Record<CompareState, string> = {
  same: 'совпадает',
  shifted: 'в другой позиции',
  absent: 'будет убрано',
}
const COMPARE_CLASS: Record<CompareState, string> = {
  same: 'text-green-700 dark:text-green-400',
  shifted: 'text-amber-700 dark:text-amber-400',
  absent: 'text-red-700 dark:text-red-400',
}

const ORIGIN_ICON: Record<RibbonOrigin, React.ComponentType<{ className?: string }>> = {
  funnel: Workflow,
  series: Workflow,
  manual: Upload,
  mp_import: Store,
}

const hasFiles = (event: React.DragEvent): boolean =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files')

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(file)
  })

interface SlotMenuState {
  index: number
  left: number
  top: number
}

export const MediaRibbon: React.FC<MediaRibbonProps> = ({
  urls,
  onChange,
  article,
  cabinet,
  renderImage,
  resolveSrc = (url) => url,
  onOpen,
  maxCount,
  hint,
  uploadEndpoint = '/marketplace/visuals/manual',
  onUploaded,
  renderMeta,
  canRemove,
  removeDisabledReason,
  emptyText = 'Ни одной фотографии — карточка покажется покупателю пустым местом',
  disabled = false,
  aspect = '3:4',
  baselineUrls,
  originOf,
  roleOf,
  remoteUrls,
  scopes,
  scope,
  onScopeChange,
  scopeContent,
  affectedCards,
  affectedText,
  onSave,
  saveLabel = 'Сохранить…',
  saving = false,
  saveDisabled = false,
}) => {
  const fileRef = useRef<HTMLInputElement>(null)
  const replaceRef = useRef<HTMLInputElement>(null)
  const replaceIndexRef = useRef<number | null>(null)
  const slotRefs = useRef<Array<HTMLDivElement | null>>([])
  const focusAfter = useRef<number | null>(null)
  const urlsRef = useRef(urls)
  urlsRef.current = urls

  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)
  const [addOver, setAddOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState(0)
  const [menu, setMenu] = useState<SlotMenuState | null>(null)
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [compare, setCompare] = useState(false)
  const [replaced, setReplaced] = useState<Map<string, string>>(() => new Map())

  // Исходный набор сменился (сохранили, перечитали): запомненные замены устарели.
  const baselineKey = baselineUrls ? baselineUrls.join('\n') : null
  useEffect(() => {
    setReplaced(new Map())
  }, [baselineKey])

  const cursor = Math.min(selected, Math.max(urls.length - 1, 0))

  const image = (src: string, alt: string, className: string) =>
    renderImage ? renderImage(src, alt, className) : <img src={src} alt={alt} className={className} />

  const diff = useMemo(() => diffRibbon(baselineUrls, urls, replaced), [baselineUrls, urls, replaced])

  const keys = useMemo(() => {
    const seen = new Map<string, number>()
    return urls.map((url) => {
      const count = seen.get(url) ?? 0
      seen.set(url, count + 1)
      return count === 0 ? url : `${url}#${count}`
    })
  }, [urls])

  // Фокус после перестановки с клавиатуры: плитка перерисовалась под новым номером.
  useEffect(() => {
    if (focusAfter.current === null) return
    slotRefs.current[focusAfter.current]?.focus()
    focusAfter.current = null
  }, [urls])

  const overLimit = maxCount && urls.length > maxCount ? urls.length - maxCount : 0
  const removable = (url: string, index: number) =>
    !disabled && (canRemove ? canRemove(url, index) : true)

  const move = (from: number, to: number): boolean => {
    if (disabled || to < 0 || to >= urls.length || from === to) return false
    const next = [...urls]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    onChange(next)
    return true
  }

  const makeMain = (index: number) => {
    if (move(index, 0)) {
      setSelected(0)
      focusAfter.current = 0
    }
  }

  const remove = (index: number) => {
    if (!removable(urls[index], index)) return
    const next = urls.filter((_, position) => position !== index)
    onChange(next)
    setSelected(Math.min(index, Math.max(next.length - 1, 0)))
  }

  const openAt = (index: number) => {
    setSelected(index)
    if (onOpen) onOpen(index)
    else setLightbox(index)
  }

  const download = async (index: number) => {
    const url = urls[index]
    if (!url) return
    setError(null)
    try {
      await downloadAuthorizedMedia(resolveSrc(url), fileNameOf(url))
    } catch (downloadError: any) {
      setError(downloadError?.message || 'Не удалось скачать файл')
    }
  }

  /**
   * Загрузка своего файла.
   *
   * Кладётся в отдельное поддерево `manual/<кабинет>/` с меткой времени в имени:
   * перегенерация визуалов пишет в свои папки и этот файл не тронет, а метка
   * исключает совпадение имён. Обратная сторона: автоматика о нём не знает —
   * он живёт только в наборе той карточки, куда его добавили.
   */
  const uploadOne = async (file: File): Promise<string | null> => {
    setError(null)
    if (!ACCEPTED.includes(file.type)) {
      setError('Площадка принимает JPG, PNG и WEBP')
      return null
    }
    if (file.size > MAX_BYTES) {
      setError(`Файл больше ${Math.round(MAX_BYTES / 1024 / 1024)} МБ — площадка такой не примет`)
      return null
    }
    setUploading(true)
    try {
      const base64 = await readAsDataUrl(file)
      /*
        Публикуем тем же каналом, что и визуалы конвейера. Прежний вызов
        `/pipeline/product/:article/visual` клал файл во внутреннее хранилище
        ПИМа и отдавал адрес за гейтвеем — площадка такую ссылку не скачивает,
        и сборщик её отбрасывал: файл загружался, но в выгрузку не попадал.
      */
      const response = await api.post(uploadEndpoint, {
        article,
        cabinet: cabinet || undefined,
        image: base64,
        format: (file.name.split('.').pop() || 'jpg').toLowerCase(),
      })
      const result = response?.data?.data ?? response?.data
      if (result?.ok === false) throw new Error(result.error || 'Сервис отклонил файл')
      const url = result?.url
      if (!url) throw new Error('Сервис не вернул адрес файла')
      // Раздача может подхватить файл не мгновенно. Молчать об этом нельзя:
      // карточка с неподтверждённым адресом уедет и получит отказ площадки.
      if (result?.verified === false) {
        setError('Файл загружен, но публичный адрес пока не отвечает — проверьте перед выгрузкой')
      }
      onUploaded?.({
        url: String(url),
        sha256: result?.sha256 ?? null,
        verified: result?.verified,
        file,
      })
      return String(url)
    } catch (uploadError: any) {
      setError(
        uploadError?.response?.data?.error?.message
          || uploadError?.message
          || 'Не удалось загрузить файл',
      )
      return null
    } finally {
      setUploading(false)
    }
  }

  const addFile = async (file: File) => {
    if (disabled) return
    if (maxCount && urlsRef.current.length >= maxCount) {
      setError(`Больше ${maxCount} фотографий площадка не принимает`)
      return
    }
    const url = await uploadOne(file)
    if (url) onChange([...urlsRef.current, url])
  }

  const replaceFile = async (index: number, file: File) => {
    if (disabled) return
    const previous = urlsRef.current[index]
    if (previous === undefined) return
    const url = await uploadOne(file)
    if (!url) return
    const current = urlsRef.current
    // Пока файл грузился, набор мог измениться: ищем кадр по адресу, а не по номеру.
    const at = current[index] === previous ? index : current.indexOf(previous)
    if (at === -1) {
      onChange([...current, url])
      return
    }
    setReplaced((map) => {
      const next = new Map(map)
      next.set(url, map.get(previous) ?? previous)
      return next
    })
    const next = [...current]
    next[at] = url
    onChange(next)
  }

  const pickFiles = async (files: FileList | File[] | null | undefined) => {
    for (const file of Array.from(files ?? [])) {
      // Последовательно: проверка предела и порядок кадров зависят от предыдущего файла.
      // eslint-disable-next-line no-await-in-loop
      await addFile(file)
    }
  }

  const openMenu = (index: number, anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect()
    setSelected(index)
    setMenu({
      index,
      left: Math.max(8, Math.min(window.innerWidth - 248, rect.right - 232)),
      top: rect.bottom + 4,
    })
  }

  const onSlotKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.target !== event.currentTarget) return
    const key = event.key
    if (event.altKey && (key === 'ArrowLeft' || key === 'ArrowRight')) {
      // Alt+←/→ окна — «Назад», а ←/→ листают карточки: слот забирает их себе.
      event.preventDefault()
      event.stopPropagation()
      const target = index + (key === 'ArrowLeft' ? -1 : 1)
      if (move(index, target)) {
        setSelected(target)
        focusAfter.current = target
      }
      return
    }
    if (event.ctrlKey || event.metaKey) return
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault()
      event.stopPropagation()
      const target = Math.min(Math.max(index + (key === 'ArrowLeft' ? -1 : 1), 0), urls.length - 1)
      setSelected(target)
      slotRefs.current[target]?.focus()
      return
    }
    if (key === 'Home' || key === 'End') {
      event.preventDefault()
      event.stopPropagation()
      const target = key === 'Home' ? 0 : urls.length - 1
      setSelected(target)
      slotRefs.current[target]?.focus()
      return
    }
    if (key === 'Delete') {
      event.preventDefault()
      event.stopPropagation()
      remove(index)
      return
    }
    if (key === 'ContextMenu' || (event.shiftKey && key === 'F10')) {
      event.preventDefault()
      event.stopPropagation()
      const anchor = event.currentTarget.querySelector<HTMLElement>('[aria-haspopup="menu"]')
      if (anchor) openMenu(index, anchor)
      return
    }
    if (key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      openAt(index)
    }
  }

  const onSlotDrop = (event: React.DragEvent, index: number) => {
    event.preventDefault()
    setOverIndex(null)
    if (hasFiles(event)) {
      const file = event.dataTransfer.files?.[0]
      if (file) void replaceFile(index, file)
      return
    }
    if (dragIndex !== null) move(dragIndex, index)
    setDragIndex(null)
  }

  const remoteRows = useMemo(
    () => (remoteUrls ? compareRemote(remoteUrls, urls) : []),
    [remoteUrls, urls],
  )
  const remoteIndex = useMemo(() => {
    const map = new Map<string, number>()
    remoteUrls?.forEach((url, index) => {
      if (!map.has(url)) map.set(url, index + 1)
    })
    return map
  }, [remoteUrls])

  const affected = affectedText
    ?? (affectedCards !== undefined && affectedCards !== null ? affectedCardsText(affectedCards) : null)

  const menuIndex = menu?.index ?? -1
  const menuUrl = menu ? urls[menu.index] : undefined
  const menuMeta = menuUrl ? deriveVisualMeta(menuUrl) : null
  const menuOrigin = menu && menuUrl ? (originOf?.(menuUrl, menu.index) ?? menuMeta?.origin ?? null) : null
  const regenReason =
    menuMeta?.origin === 'series'
      ? REGEN_SERIES_REASON
      : menuOrigin === 'manual'
        ? REGEN_MANUAL_REASON
        : REGEN_FUNNEL_REASON

  const scopeBar = scopes && scopes.length > 0 && (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <SegmentedControl
        size="sm"
        aria-label="Охват правки"
        value={scope ?? scopes[0].key}
        onChange={(key) => onScopeChange?.(key)}
        options={scopes.map((item) => ({
          value: item.key,
          label: item.label,
          hint: item.hint,
          disabled: item.disabled,
        }))}
      />
      {affected && !scopeContent && (
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          затронет <b className="font-semibold text-gray-800 dark:text-zinc-100">{affected}</b>
        </span>
      )}
    </div>
  )

  const closeMenu = useCallback(() => setMenu(null), [])

  return (
    <div className="space-y-3">
      {hint}
      {scopeBar}

      {scopeContent ?? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              multiple
              disabled={disabled}
              accept={ACCEPTED.join(',')}
              className="hidden"
              onChange={(event) => {
                void pickFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <input
              ref={replaceRef}
              type="file"
              disabled={disabled}
              accept={ACCEPTED.join(',')}
              className="hidden"
              aria-label="Файл для замены кадра"
              onChange={(event) => {
                const file = event.target.files?.[0]
                const index = replaceIndexRef.current
                if (file && index !== null) void replaceFile(index, file)
                event.target.value = ''
              }}
            />
            <button
              type="button"
              disabled={disabled || uploading || !article}
              onClick={() => fileRef.current?.click()}
              onDragOver={(event) => {
                if (disabled || !hasFiles(event)) return
                event.preventDefault()
                setAddOver(true)
              }}
              onDragLeave={() => setAddOver(false)}
              onDrop={(event) => {
                setAddOver(false)
                if (disabled || !hasFiles(event)) return
                event.preventDefault()
                void pickFiles(event.dataTransfer.files)
              }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800',
                addOver
                  ? 'border-indigo-500 ring-2 ring-indigo-300 dark:ring-indigo-800'
                  : 'border-gray-300 dark:border-zinc-700',
              )}
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
              Добавить
            </button>
            {remoteUrls && (
              <button
                type="button"
                aria-pressed={compare}
                onClick={() => setCompare((value) => !value)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-gray-50 dark:hover:bg-zinc-800',
                  compare
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                    : 'border-gray-300 bg-white text-gray-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200',
                )}
              >
                <Columns2 className="h-3.5 w-3.5" />
                {compare ? 'Скрыть сравнение' : 'Сравнить с площадкой'}
              </button>
            )}
            <span className="text-[11px] text-gray-400 dark:text-zinc-500">
              {urls.length}
              {maxCount ? ` из ${maxCount}` : ''} · порядок задаёт перетаскивание, первое фото — главное
              {!disabled && urls.length > 1 ? ' · Alt+←/→ двигает выбранный кадр' : ''}
            </span>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}
          {overLimit > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Больше {maxCount} фотографий площадка не примет: уберите {overLimit}
            </div>
          )}

          {urls.length ? (
            <div
              role="list"
              aria-label="Визуалы в порядке отправки"
              className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4"
            >
              {urls.map((url, index) => {
                const meta = deriveVisualMeta(url)
                const origin = originOf?.(url, index) ?? meta.origin
                const role = roleOf?.(url, index) ?? meta.role
                const mark = diff.marks.get(index)
                const beyond = Boolean(maxCount && index >= maxCount)
                const onRemote = remoteIndex.get(url)
                const OriginIcon = origin ? ORIGIN_ICON[origin] : null
                const isSelected = index === cursor
                const name = fileNameOf(url)
                return (
                  <div
                    key={keys[index]}
                    ref={(element) => {
                      slotRefs.current[index] = element
                    }}
                    role="listitem"
                    tabIndex={isSelected ? 0 : -1}
                    aria-label={`Слот ${index + 1}${role ? `: ${role}` : ''}`}
                    data-slot-index={index}
                    draggable={!disabled}
                    onFocus={(event) => {
                      if (event.target === event.currentTarget) setSelected(index)
                    }}
                    onKeyDown={(event) => onSlotKeyDown(event, index)}
                    onDragStart={(event) => {
                      setDragIndex(index)
                      try {
                        event.dataTransfer.setData('text/plain', String(index))
                        event.dataTransfer.effectAllowed = 'move'
                      } catch {
                        /* в тестовой среде dataTransfer урезан */
                      }
                    }}
                    onDragOver={(event) => {
                      if (disabled) return
                      event.preventDefault()
                      setOverIndex(index)
                    }}
                    onDragLeave={() => setOverIndex((current) => (current === index ? null : current))}
                    onDrop={(event) => onSlotDrop(event, index)}
                    onDragEnd={() => {
                      setDragIndex(null)
                      setOverIndex(null)
                    }}
                    className={cn(
                      'group relative space-y-1.5 rounded-lg p-1 outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-indigo-500',
                      !disabled && 'cursor-grab active:cursor-grabbing',
                      isSelected && 'ring-2 ring-indigo-300 dark:ring-indigo-800',
                      dragIndex === index && 'opacity-40',
                      overIndex === index && dragIndex !== index && 'ring-2 ring-indigo-500',
                    )}
                  >
                    <div className="relative">
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`Открыть визуал ${index + 1} крупно`}
                        onClick={() => openAt(index)}
                        className="block w-full"
                      >
                        {image(
                          resolveSrc(url),
                          `Визуал ${index + 1}`,
                          `${ASPECT_CLASS[aspect]} w-full rounded-md border border-gray-200 object-contain dark:border-zinc-800`,
                        )}
                      </button>
                      <span className="pointer-events-none absolute left-1.5 top-1.5 rounded bg-gray-950/70 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
                        {index + 1}
                      </span>
                      {mark && (
                        <span
                          className={cn(
                            'pointer-events-none absolute left-1.5 top-7 rounded px-1.5 py-0.5 text-[10px] font-medium',
                            MARK_CLASS[mark],
                          )}
                        >
                          {MARK_LABEL[mark]}
                        </span>
                      )}
                      {index === 0 && (
                        <span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-indigo-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          главное
                        </span>
                      )}
                      {beyond && (
                        <span className="pointer-events-none absolute bottom-1.5 right-1.5 rounded bg-red-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          сверх лимита
                        </span>
                      )}
                      {renderMeta?.(url, index)}
                      <button
                        type="button"
                        title={
                          canRemove && !canRemove(url, index)
                            ? removeDisabledReason || 'Этот кадр нельзя убрать'
                            : 'Убрать из выгрузки'
                        }
                        aria-label={`Убрать визуал ${index + 1}`}
                        tabIndex={isSelected ? 0 : -1}
                        disabled={!removable(url, index)}
                        onClick={() => remove(index)}
                        className="absolute right-1.5 top-1.5 rounded-md bg-gray-950/70 p-1.5 text-white opacity-0 transition-opacity hover:bg-red-600 disabled:cursor-not-allowed disabled:hover:bg-gray-950/70 group-hover:opacity-100 focus-visible:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex items-start gap-1">
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div
                          className={cn(
                            'truncate text-[11px]',
                            role
                              ? 'font-medium text-gray-700 dark:text-zinc-200'
                              : 'font-mono text-[10px] text-gray-500 dark:text-zinc-400',
                          )}
                          title={url}
                        >
                          {role ?? name}
                        </div>
                        {origin && OriginIcon && (
                          <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-zinc-400">
                            <OriginIcon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{ORIGIN_LABEL[origin]}</span>
                          </div>
                        )}
                        {compare && remoteUrls && (
                          <div
                            className={cn(
                              'text-[10px]',
                              onRemote === index + 1
                                ? COMPARE_CLASS.same
                                : onRemote
                                  ? COMPARE_CLASS.shifted
                                  : 'text-gray-500 dark:text-zinc-400',
                            )}
                          >
                            {onRemote === index + 1
                              ? 'на площадке в этой позиции'
                              : onRemote
                                ? `на площадке № ${onRemote}`
                                : 'на площадке нет'}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        aria-label={`Действия со слотом ${index + 1}`}
                        tabIndex={isSelected ? 0 : -1}
                        aria-haspopup="menu"
                        aria-expanded={menuIndex === index}
                        onClick={(event) => openMenu(index, event.currentTarget)}
                        className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-400 dark:border-zinc-700">
              {emptyText}
            </div>
          )}

          {compare && remoteUrls && (
            <section aria-label="На площадке сейчас" className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-zinc-200">На площадке сейчас</h4>
              {remoteRows.length ? (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 xl:grid-cols-6">
                  {remoteRows.map((row, index) => (
                    <div key={`${row.url}-${index}`} className="space-y-1">
                      <div className="relative">
                        {image(
                          resolveSrc(row.url),
                          `На площадке ${index + 1}`,
                          `${ASPECT_CLASS[aspect]} w-full rounded-md border border-gray-200 object-contain dark:border-zinc-800`,
                        )}
                        <span className="pointer-events-none absolute left-1.5 top-1.5 rounded bg-gray-950/70 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
                          {index + 1}
                        </span>
                      </div>
                      <div className={cn('text-[10px]', COMPARE_CLASS[row.state])}>
                        {COMPARE_LABEL[row.state]}
                        {row.state === 'shifted' && row.at ? ` · в ленте № ${row.at}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 dark:text-zinc-500">На площадке сейчас нет фотографий</p>
              )}
            </section>
          )}
        </>
      )}

      {diff.changed && !scopeContent && (
        <div
          role="status"
          className="sticky bottom-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 shadow-sm dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100"
        >
          <Pencil className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            Изменено: {diff.summary.join(', ')}
            {affected ? ` · затронет ${affected}` : ''}
          </span>
          <Button
            size="sm"
            variant="secondary"
            aria-label="Отменить правки визуалов"
            disabled={disabled || !baselineUrls}
            onClick={() => {
              setReplaced(new Map())
              if (baselineUrls) onChange([...baselineUrls])
            }}
          >
            Отменить
          </Button>
          {onSave && (
            <Button size="sm" loading={saving} disabled={saveDisabled || disabled} onClick={onSave}>
              {saveLabel}
            </Button>
          )}
        </div>
      )}

      {menu && menuUrl !== undefined && (
        <SlotMenu
          left={menu.left}
          top={menu.top}
          onClose={closeMenu}
          items={[
            {
              key: 'main',
              label: 'Сделать главным',
              icon: <Star className="h-3.5 w-3.5" />,
              disabled: disabled || menu.index === 0,
              onSelect: () => makeMain(menu.index),
            },
            {
              key: 'replace',
              label: 'Заменить…',
              icon: <Replace className="h-3.5 w-3.5" />,
              disabled: disabled || uploading || !article,
              onSelect: () => {
                replaceIndexRef.current = menu.index
                replaceRef.current?.click()
              },
            },
            {
              key: 'regen',
              label: 'Перегенерировать слайд',
              icon: <Sparkles className="h-3.5 w-3.5" />,
              disabled: true,
              reason: regenReason,
            },
            {
              key: 'download',
              label: 'Скачать',
              icon: <Download className="h-3.5 w-3.5" />,
              onSelect: () => void download(menu.index),
            },
            {
              key: 'zoom',
              label: 'Открыть крупно',
              icon: <Maximize2 className="h-3.5 w-3.5" />,
              onSelect: () => openAt(menu.index),
            },
            {
              key: 'remove',
              label: 'Удалить из набора',
              icon: <Trash2 className="h-3.5 w-3.5" />,
              danger: true,
              separated: true,
              disabled: !removable(menuUrl, menu.index),
              reason: canRemove && !disabled && !canRemove(menuUrl, menu.index)
                ? removeDisabledReason || 'Этот кадр нельзя убрать'
                : undefined,
              onSelect: () => remove(menu.index),
            },
          ]}
        />
      )}

      {lightbox !== null && (
        <AuthorizedLightbox
          isOpen
          sources={urls.map((url) => resolveSrc(url))}
          index={Math.min(lightbox, urls.length - 1)}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
}

interface SlotMenuItem {
  key: string
  label: string
  icon: React.ReactNode
  disabled?: boolean
  reason?: string
  danger?: boolean
  separated?: boolean
  onSelect?: () => void
}

/**
 * Меню слота «⋯». Живёт в портале поверх окна, закрывается кликом вне, Esc и
 * прокруткой. Esc не уходит дальше: окно сущности иначе закрылось бы вместе с меню.
 */
const SlotMenu: React.FC<{
  left: number
  top: number
  items: SlotMenuItem[]
  onClose: () => void
}> = ({ left, top, items, onClose }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button:not([aria-disabled="true"])')?.focus()
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose()
    }
    const onScroll = () => onClose()
    document.addEventListener('mousedown', onDown)
    window.addEventListener('resize', onScroll)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('resize', onScroll)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [onClose])

  const focusStep = (direction: 1 | -1) => {
    const buttons = Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>('button:not([aria-disabled="true"])') ?? [],
    )
    if (!buttons.length) return
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement)
    buttons[(at + direction + buttons.length) % buttons.length]?.focus()
  }

  // Портал через document.body, как у остальных выпадающих меню окна.
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Действия со слотом"
      style={{ left, top }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onClose()
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault()
          event.stopPropagation()
          focusStep(event.key === 'ArrowDown' ? 1 : -1)
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.stopPropagation()
        }
      }}
      className="fixed z-[75] w-60 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
    >
      {items.map((item) => (
        <React.Fragment key={item.key}>
          {item.separated && <hr className="my-1 border-gray-200 dark:border-zinc-800" />}
          <button
            type="button"
            role="menuitem"
            aria-disabled={item.disabled || undefined}
            title={item.reason}
            onClick={() => {
              if (item.disabled) return
              onClose()
              item.onSelect?.()
            }}
            className={cn(
              'flex w-full items-start gap-2 px-3 py-1.5 text-left text-sm',
              item.disabled
                ? 'cursor-not-allowed text-gray-400 dark:text-zinc-600'
                : item.danger
                  ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800',
            )}
          >
            <span className="mt-0.5 shrink-0">{item.icon}</span>
            <span className="min-w-0">
              {item.label}
              {item.disabled && item.reason && (
                <span className="mt-0.5 block text-[11px] leading-snug text-gray-400 dark:text-zinc-500">
                  {item.reason}
                </span>
              )}
            </span>
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body,
  )
}

export default MediaRibbon
